import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { message } from "antd";
import { AuthProvider, RequireAuth, RequireNoAuth } from "./helpers/auth";
import "./App.less";
import { MainContainer } from "./components/navbar/MainContainer";
import { SignIn } from "./pages/signin/SignInPage";
import { SignUp } from "./pages/signup/SignUpPage";
import { GithubLogin } from "./pages/signin/GithubLoginPage";
import { Projects } from "./pages/project/ProjectListPage";
import { Project } from "./pages/project/ProjectPage";
import { SubgraphDetail } from "./pages/subgraph/SubgraphDetailPage";
import { Docs } from "./pages/doc/Docs";
import { Profile } from "./pages/profile/Profile";
import { Token } from "./helpers/session";
import { API_BASEURL } from "./config/StaticConfig";
import { JSError } from "./components/error/JSError";
import { NotFound } from "./components/error/NotFound";
import { Dashboard } from "./pages/dashboard/DashboardPage";

const httpLink = createHttpLink({
  uri: `${API_BASEURL}/graphql`,
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: Token.has() ? `Bearer ${Token.get()}` : "",
    },
  };
});

// error handling for all GraphQL request
const errorLink = onError(({ graphQLErrors, response }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ extensions, message: errorMessage }) => {
      if (extensions.code === "UNAUTHORIZED") {
        Token.remove();
        message.error("Your session has expired. Please relogin.", 2, () => {
          window.location.reload();
        });
      } else if (!response || !response.data) {
        message.error(errorMessage.toString());
      }
    });
  }
});

const client = new ApolloClient({
  link: authLink.concat(errorLink).concat(httpLink),
  cache: new InMemoryCache(),
});

const App = () => {
  return (
    <AuthProvider>
      <ApolloProvider client={client}>
        <BrowserRouter>
          <JSError>
            <Routes>
              <Route
                path="github/callback"
                element={
                  <RequireNoAuth>
                    <GithubLogin />
                  </RequireNoAuth>
                }
              />
              <Route
                path="signin"
                element={
                  <RequireNoAuth>
                    <SignIn />
                  </RequireNoAuth>
                }
              />
              <Route
                path="signup"
                element={
                  <RequireNoAuth>
                    <SignUp />
                  </RequireNoAuth>
                }
              />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <MainContainer />
                  </RequireAuth>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/:id/subgraphs" element={<Project />} />
                <Route path="projects/:id/subgraphs-v2" element={<Project />} />
                <Route path="projects/:id/endpoints" element={<Project />} />
                <Route
                  path="projects/:id/endpoints/:chain/:network/:nodeType"
                  element={<Project />}
                />
                <Route path="projects/:id/collaborators" element={<Project />} />
                <Route path="projects/:id/settings" element={<Project />} />
                <Route
                  path="projects/:projectId/subgraphs/:subgraphNamespace/:subgraphIdentifier"
                  element={<SubgraphDetail />}
                />
                <Route
                  path="projects/:projectId/subgraphs-v2/:subgraphNamespace/:subgraphIdentifier"
                  element={<SubgraphDetail />}
                />
                <Route path="profile" element={<Profile />} />
                <Route path="docs" element={<Docs />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </JSError>
        </BrowserRouter>
      </ApolloProvider>
    </AuthProvider>
  );
};

export default App;
